import axios, { type AxiosRequestConfig } from 'axios'
import { getAuthClient } from '@/features/authentication/utils/authentication/client'
import { getApiGatewayURL, getRequestParams } from '@/features/shared/utils/dataFetching/url'
import { type StoreParams } from '@/features/shared/utils/dataFetching/storeParams'
import { getAnonymousId } from '@shipt/analytics-member-web'
import { getAnonymousIdOnServer } from '@/features/shared/serverUtils/getAnonymousIdOnServer'
import {
  buildErrorMetadata,
  logDataFetchingErrors,
} from '@/features/shared/utils/dataFetching/utils'
import { API_URL } from '@/features/shared/utils/dataFetching/apiUrl'
import { getUniversalId } from '@/features/shared/services/Experiments/utils'
import { isOnServer } from '@shared/constants/util'
import { getDataRightsTrait } from '@/features/account/services/DataPrivacy/utils'
import { getCommonRequestHeaders } from '@/features/shared/utils/dataFetching/requestHeaders'
import { getActiveGroupId } from '@/features/business/utils/getActiveGroupId'
import { logInfo } from '@/features/shared/utils/logger'
import { type QueryContext } from '@/features/shared/utils/dataFetching/types'
import { getServerAccessTokenWithContext } from '@/features/shared/utils/dataFetching/accessToken'
;(() => {
  // Log this once at module load time to declare the gateway URL being used (server-side only)
  if (isOnServer()) {
    logInfo(`Declaration: Gateway URL`, { gatewayUrl: getApiGatewayURL() })
  }
})()

export type MarvAdviceByCampaign = {
  marv_advice_by_campaign?: Record<string, string>
}

type ClientOptions = {
  apiUrl?: API_URL
  includeAuthHeader?: boolean
  includeDefaultParams?: boolean
  overrideUrl?: string
  /** Error messages that should be logged as info instead of error */
  ignoredMessages?: (string | RegExp)[]
  /**
   * When set, forces the `X-Group-Id-Ext` header to this value regardless of
   * whether the user is actively switched into a persona/group. Useful for
   * making API calls on behalf of a group before fully switching into it
   * (e.g. during business profile creation).
   */
  overrideGroupId?: string
}

export type ApiClient = {
  config?: AxiosRequestConfig & {
    onErrorLog?: Record<string, unknown>
    /** This object forces logging of any nested object rather than omit it */
    forceLogTheseKeys?: Record<string, boolean>
    // this allows an engineer to set data that should be redacted for PII or other reasons
    redaction?: string[]
  }
  options?: ClientOptions & { storeParams?: StoreParams }
  context?: QueryContext
  /** fetcherName metadata field on errors. Use to easily tie API errors to calling code*/
  fetcherName: string
  /** whether error logs for this API should be grouped by individual status codes */
  groupErrorsByStatusCode?: boolean
  /** tag metadata field on errors. Use for easier bookmarking of related endpoints*/
  tag?: string
}

type GetBaseURLConfig = {
  options?: ClientOptions
}

export const getBaseURL = ({ options }: GetBaseURLConfig = {}) => {
  const { apiUrl = API_URL.API_GATEWAY, overrideUrl } = options ?? {}
  if (overrideUrl) {
    return overrideUrl
  }

  switch (apiUrl) {
    case API_URL.SELF_API:
      return '/api'
    case API_URL.API_GATEWAY:
      return getApiGatewayURL()
    default:
      throw new Error(`Unsupported apiUrl passed: "${apiUrl}"`)
  }
}

const createClientFetch = ({ options }: GetBaseURLConfig) => {
  const baseURL = getBaseURL({ options })
  if (!baseURL) {
    // this error indicates an ignored upstream typescript violation.
    // maybe we should invent test cases that can reach this error, then fix those bugs, then remove this.
    // optional `undefined`-parameter values are fine; they will still use the initializer.  See:
    // https://www.typescriptlang.org/docs/handbook/2/functions.html#optional-parameters
    throw new Error(`Unsupported apiUrl passed: "${options?.apiUrl}"`)
  }
  return <T>(config: AxiosRequestConfig) => {
    const requestConfig: AxiosRequestConfig = {
      baseURL,
      ...config,
    }
    return axios.request<T>(requestConfig)
  }
}

export const clientConfig = async ({
  config = {},
  options,
  context,
}: ApiClient): Promise<AxiosRequestConfig> => {
  const {
    storeParams = {},
    includeAuthHeader = true,
    includeDefaultParams = true,
    overrideGroupId,
  } = options ?? {}

  const contextMeta = context?.meta

  const headers: AxiosRequestConfig['headers'] = {
    ...getCommonRequestHeaders({
      cookies: contextMeta?.cookies,
      headers: contextMeta?.headers,
    }),
    ...config.headers,
  }

  const universal_id = getUniversalId({
    serverCookies: contextMeta?.cookies,
  })

  if (includeAuthHeader) {
    try {
      const accessToken = isOnServer()
        ? await getServerAccessTokenWithContext(context, config.url ?? '')
        : await getAuthClient().getAccessToken()
      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`
        const activeGroupId =
          overrideGroupId ??
          getActiveGroupId({
            cookies: contextMeta?.cookies,
          })
        if (activeGroupId) {
          headers['X-Group-Id-Ext'] = activeGroupId
        }
      }
    } catch {
      // porting existing behavior for now; do nothing if there is an error getting access token
      // api call will fail; error from failed fetch will log
    }
  }

  if (!includeDefaultParams) {
    return {
      ...config,
      headers,
    }
  }

  const params: AxiosRequestConfig['params'] = {
    anonymous_id: contextMeta?.cookies
      ? getAnonymousIdOnServer(contextMeta.cookies)
      : getAnonymousId(),
    ...(universal_id && { universal_id }),
    ...config.params,
    ...getRequestParams(storeParams),
    ...getDataRightsTrait({
      cookies: context?.meta?.cookies,
      queryClient: context?.client,
    }),
    platform: 'web',
  }

  // ungated endpoints can't accept user_id as a param or else they will
  // assume the user is authenticated and perform different validations
  if (!Number(params.user_id)) delete params.user_id
  return {
    ...config,
    headers,
    params,
  }
}

type FormattedResponse<T> = T extends null | undefined ? T : T & MarvAdviceByCampaign

export const getApiClient = async <ApiResponse>(
  apiClient: ApiClient,
): Promise<FormattedResponse<ApiResponse>> => {
  const contextMeta = apiClient.context?.meta
  const clientFetch = createClientFetch({ options: apiClient.options })
  const redaction = apiClient.config?.redaction || []
  const config = await clientConfig(apiClient)

  const redactedData = { ...config.data }
  if (redaction.length > 0) {
    Object.keys(redactedData).forEach((item) => {
      if (redaction.includes(item)) {
        redactedData[item] = 'redacted'
      }
    })
  }
  try {
    const results = await clientFetch<FormattedResponse<ApiResponse>>(config)
    return results.data
  } catch (error) {
    if (axios.isCancel(error)) throw error

    const errorMetadata = buildErrorMetadata({
      rootObj: config.params ?? {},
      payloadObj: redactedData,
      allowObjKeys: apiClient.config?.forceLogTheseKeys,
    })

    logDataFetchingErrors({
      apiClient,
      config,
      error,
      requestContextHeaders: contextMeta?.headers,
      meta: errorMetadata,
    })

    throw error
  }
}

export const apiGet = <TResponse>({ config, ...rest }: ApiClient) =>
  getApiClient<TResponse>({ config: { ...config, method: 'GET' }, ...rest })

export const apiDelete = <TResponse>({ config, ...rest }: ApiClient) =>
  getApiClient<TResponse>({ config: { ...config, method: 'DELETE' }, ...rest })

export const apiPost = <TResponse>({ config, ...rest }: ApiClient) =>
  getApiClient<TResponse>({ config: { ...config, method: 'POST' }, ...rest })

export const apiPut = <TResponse>({ config, ...rest }: ApiClient) =>
  getApiClient<TResponse>({ config: { ...config, method: 'PUT' }, ...rest })

export const apiPatch = <TResponse>({ config, ...rest }: ApiClient) =>
  getApiClient<TResponse>({ config: { ...config, method: 'PATCH' }, ...rest })
